import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslations, useLocale } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { pickLocalized } from "@/lib/i18n/localized";
import type { ProductListResponse } from "@/lib/api/types";
import { SpotlightCard } from "@/shared/components/SpotlightCard";
import {
  getProductCardAttention,
  type ProductCardStatus,
} from "@/modules/products/lib/cardAttention";
import { ProductAttentionStrip } from "@/modules/products/components/ProductAttentionStrip";
import { ProductActionsMenu } from "@/modules/products/components/ProductActionsMenu";
import { ProductSelectCheckbox } from "@/modules/products/components/ProductSelectCheckbox";

export type { ProductCardStatus } from "@/modules/products/lib/cardAttention";

const STATUS_LABEL_KEY: Record<ProductCardStatus, string> = {
  active: keys.products.statusActive,
  low_stock: keys.products.statusLowStock,
  out_of_stock: keys.products.statusOutOfStock,
  hidden: keys.products.statusHiddenShort,
};

const STATUS_DOT: Record<ProductCardStatus, string> = {
  active: "#16A34A",
  low_stock: "#D97706",
  out_of_stock: "#E5484D",
  hidden: "var(--text-disabled)",
};

const STATUS_FG: Record<ProductCardStatus, string> = {
  active: "var(--success-500)",
  low_stock: "var(--warning-500)",
  out_of_stock: "var(--error-500)",
  hidden: "var(--text-muted)",
};

const STATUS_BG: Record<ProductCardStatus, string> = {
  active: "color-mix(in oklab, #16A34A 12%, transparent)",
  low_stock: "color-mix(in oklab, #D97706 12%, transparent)",
  out_of_stock: "color-mix(in oklab, #E5484D 12%, transparent)",
  hidden: "var(--surface-alt)",
};

type Props = {
  product: ProductListResponse["items"][number];
  status: ProductCardStatus;
  category: string | null;
  thumbnail: string | null;
  currentPrice: string;
  originalPrice: string | null;
  discountPct: number | null;
  stockTotal: number;
  /** Unlimited-stock products report a 0 stock total but are never out of
   *  stock — this keeps the stock line from reading "out of stock". Required
   *  on purpose: an optional flag defaulting to false silently reintroduces
   *  the bug at any call site that forgets it. */
  isUnlimitedStock: boolean;
  stockMax?: number;
  /** "12 sold · 30d" — optional. Pass null/undefined to hide. */
  sold30dLabel?: string | null;
  editHref: string;
  shareHref: string;
  // ── Interactive actions (wired from the products page; optional so the
  //    lightweight `dashboardAction` mode can omit them) ──
  onToggleVisibility?: () => void;
  onRequestDelete?: () => void;
  onEditStock?: () => void;
  onAddPhoto?: (file: File) => void;
  pendingVisibility?: boolean;
  pendingPhoto?: boolean;
  // ── Bulk selection (cart link) ──
  selectMode?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
  selectAriaLabel?: string;
  /**
   * Compact "dashboard" mode: same card visual, but the share/⋯ footer, select
   * checkbox, and attention overlays are replaced by a single action button
   * (e.g. View / Restock) used by the dashboard product sections.
   */
  dashboardAction?: {
    label: string;
    href: string;
    tone: "view" | "restock";
  };
};

export function ProductsGridCard({
  product,
  status,
  category,
  thumbnail,
  currentPrice,
  originalPrice,
  discountPct,
  stockTotal,
  isUnlimitedStock,
  editHref,
  shareHref,
  onToggleVisibility = () => {},
  onRequestDelete = () => {},
  onEditStock = () => {},
  onAddPhoto = () => {},
  pendingVisibility = false,
  pendingPhoto = false,
  selectMode = false,
  selected = false,
  onToggleSelect = () => {},
  selectAriaLabel = "",
  dashboardAction,
}: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const navigate = useNavigate();
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = thumbnail !== null && !imgFailed;
  const productName = pickLocalized(locale, product.nameAr, product.nameEn);

  const stockText = isUnlimitedStock
    ? t(keys.products.editSidebarInventoryUnlimited)
    : stockTotal === 0
      ? t(keys.products.statusOutOfStock)
      : t(keys.products.mobileInStock, { count: stockTotal });

  // Split the currency prefix off the amount so "EGP" renders small next to the
  // big number, matching the design.
  const { symbol: priceSymbol, value: priceValue } = splitPrice(currentPrice);

  // "Needs attention" diagnosis — shared with the table + mobile views.
  const attention = getProductCardAttention(
    {
      status,
      hiddenReasons: product.sellerBuyerHiddenReasons ?? [],
      stockTotal,
    },
    t,
  );

  // Border + ring emphasis, reused across states for consistency (only the
  // colour changes): selected = accent, attention error = danger, attention
  // warning = gold, otherwise the plain card border.
  const edgeClass = dashboardAction
    ? "border-[color:var(--card-br)]"
    : selected
      ? "border-[color:var(--accent-500)] ring-2 ring-[color:var(--accent-500)]"
      : attention?.tone === "error"
        ? "border-[color:var(--danger-500,var(--error-500))] ring-2 ring-[color:var(--danger-500,var(--error-500))]"
        : attention
          ? "border-[color:var(--gold-500)] ring-2 ring-[color:var(--gold-500)]"
          : "border-[color:var(--card-br)]";

  const handleActivate = () => {
    if (selectMode) onToggleSelect();
    else navigate(editHref);
  };

  return (
    <SpotlightCard
      role="link"
      tabIndex={0}
      onClick={handleActivate}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          handleActivate();
        }
      }}
      aria-label={t(keys.products.editProductAria, { name: productName })}
      glow="color-mix(in oklab, var(--text-primary) 6%, transparent)"
      className={[
        "group flex cursor-pointer flex-col overflow-hidden rounded-[16px] border bg-[color:var(--surface)] shadow-[0_1px_2px_rgba(0,0,0,0.04)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--accent-500)]",
        edgeClass,
      ].join(" ")}
      tooltip={
        <>
          <div className="mb-2 whitespace-nowrap text-[12.5px] font-bold">
            {productName}
          </div>
          <div className="flex items-center justify-between gap-5 text-[11.5px]">
            <span className="text-[color:var(--text-muted)]">
              {t(keys.common.price)}
            </span>
            <span className="font-bold text-[color:var(--text-primary)]">
              <bdi>{currentPrice}</bdi>
            </span>
          </div>
          <div className="mt-1.5 flex items-center justify-between gap-5 text-[11.5px]">
            <span className="text-[color:var(--text-muted)]">
              {t(keys.products.stockLabel)}
            </span>
            <span className="font-semibold text-[color:var(--text-primary)]">
              {stockText}
            </span>
          </div>
        </>
      }
    >
      {/* thumbnail — inset rounded tile; white when a real image is present so
          background-removed product shots read cleanly. */}
      <div
        className="relative mx-[10px] mt-[10px] aspect-square h-auto overflow-hidden rounded-[14px] border border-[color:var(--border-light)] lg:aspect-auto lg:h-[190px]"
        style={{
          background: showImage ? "var(--surface)" : "var(--surface-alt)",
        }}
      >
        {showImage ? (
          <img
            src={thumbnail}
            alt=""
            className="h-full w-full object-contain p-1 lg:p-2"
            aria-hidden="true"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <>
            {/* cross-hair guides + centered product tile (empty placeholder) */}
            <div className="absolute inset-y-0 left-1/2 w-px bg-[color:var(--border-light)]" />
            <div className="absolute inset-x-0 top-1/2 h-px bg-[color:var(--border-light)]" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex h-[60px] w-[60px] items-center justify-center rounded-[15px] bg-[color:var(--surface)] text-[color:var(--text-disabled)] shadow-[0_6px_16px_rgba(0,0,0,0.07)]">
                <BagIcon />
              </div>
            </div>
          </>
        )}

        {/* Hidden products are dimmed so "not live" reads at a glance */}
        {status === "hidden" ? (
          <div aria-hidden="true" className="absolute inset-0 bg-white/55" />
        ) : null}

        {/* top-start: discount badge, or the selection checkbox in select mode */}
        {discountPct && !(selectMode || selected) ? (
          <span className="absolute start-3 top-3 rounded-full bg-[color:var(--gold-500)] px-[9px] py-1 text-[11px] font-bold text-[#1A1A19]">
            {t(keys.products.discountBadge, { pct: discountPct })}
          </span>
        ) : null}
        {selectMode || selected ? (
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute start-3 top-3 z-[2]"
          >
            <ProductSelectCheckbox
              checked={selected}
              onToggle={onToggleSelect}
              ariaLabel={selectAriaLabel}
              variant="overlay"
            />
          </div>
        ) : null}

        {/* Attention strip — overlaid on the thumbnail bottom (problem cards).
            Skipped in dashboard mode, which uses a single footer action. */}
        {attention && !dashboardAction ? (
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute inset-x-0 bottom-0"
          >
            <ProductAttentionStrip
              attention={attention}
              editHref={editHref}
              onToggleVisibility={onToggleVisibility}
              onAddPhoto={onAddPhoto}
              onEditStock={onEditStock}
              uploadingLabel={t(keys.products.uploadingPhoto)}
              pending={pendingVisibility || pendingPhoto}
              variant="overlay"
            />
          </div>
        ) : null}
      </div>

      {/* body */}
      <div className="flex flex-1 flex-col px-3.5 pb-3 pt-2.5">
        {/* name + status pill — the pill lives here, outside the image frame */}
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 truncate text-[13.5px] font-semibold leading-[1.3] text-[color:var(--text-primary)]">
            {productName}
          </div>
          <span
            className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-[9px] py-1 text-[11px] font-semibold"
            style={{ background: STATUS_BG[status], color: STATUS_FG[status] }}
          >
            <span
              aria-hidden="true"
              className="inline-block h-[5px] w-[5px] rounded-full"
              style={{ background: STATUS_DOT[status] }}
            />
            {t(STATUS_LABEL_KEY[status])}
          </span>
        </div>
        <div className="mt-1 flex min-w-0 items-center gap-1.5 text-[11.5px] text-[color:var(--text-muted)]">
          <span className="truncate">{category || "—"}</span>
          <span
            aria-hidden="true"
            className="h-[3px] w-[3px] shrink-0 rounded-full bg-[color:var(--border-strong)]"
          />
          <span className="shrink-0 whitespace-nowrap">{stockText}</span>
        </div>

        {/* price + actions footer */}
        <div className="mt-2.5 flex items-center justify-between gap-2 border-t border-[color:var(--border-light)] pt-2.5">
          <div className="flex min-w-0 items-baseline gap-1.5">
            {/* current price never shrinks; the strikethrough gives way first */}
            <span className="flex shrink-0 items-baseline gap-0.5">
              {priceSymbol ? (
                <span className="text-[10px] font-semibold text-[color:var(--text-muted)]">
                  {priceSymbol}
                </span>
              ) : null}
              <span className="text-[14px] font-bold tracking-[-0.01em] text-[color:var(--text-primary)]">
                <bdi>{priceValue}</bdi>
              </span>
            </span>
            {originalPrice ? (
              <span className="min-w-0 truncate text-[11px] text-[color:var(--text-muted)] line-through">
                {originalPrice}
              </span>
            ) : null}
          </div>
          {dashboardAction ? (
            <Link
              to={dashboardAction.href}
              onClick={(e) => e.stopPropagation()}
              className={[
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-[15px] py-[6px] text-[11.5px] font-semibold transition-colors",
                dashboardAction.tone === "restock"
                  ? "border border-[color:var(--accent-500)] bg-[color:var(--accent-500)] text-[color:var(--on-accent)] hover:bg-[color:var(--accent-600)]"
                  : "border border-[color:var(--border-light)] bg-[color:var(--surface)] text-[color:var(--text-primary)] hover:bg-[color:var(--hover)]",
              ].join(" ")}
            >
              {dashboardAction.tone === "restock" ? (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                  <path d="M3 3v5h5" />
                </svg>
              ) : null}
              {dashboardAction.label}
            </Link>
          ) : (
            <div
              className="flex shrink-0 items-center gap-1.5"
              onClick={(e) => e.stopPropagation()}
            >
              <ProductActionsMenu
                target={{
                  id: product.id,
                  name: productName,
                  isActive: product.isActive,
                }}
                editHref={editHref}
                previewHref={shareHref}
                pendingVisibility={pendingVisibility}
                onToggleVisibility={onToggleVisibility}
                onRequestDelete={onRequestDelete}
                onEditStock={onEditStock}
                triggerClassName="!h-10 !w-10 lg:!h-8 lg:!w-8 !rounded-[9px] !border !border-[color:var(--card-br)] !text-[color:var(--text-muted)]"
              />
            </div>
          )}
        </div>
      </div>
    </SpotlightCard>
  );
}

/**
 * Separate a leading currency prefix (e.g. "EGP ") from the numeric part so the
 * currency can render small next to the big amount, matching the design.
 * Locales that put the number first (Arabic) fall back to the whole string.
 */
function splitPrice(formatted: string): { symbol: string; value: string } {
  const m = formatted.match(/^([^\d]+?)\s*([\d.,\s].*)$/);
  if (m) return { symbol: m[1].trim(), value: m[2].trim() };
  return { symbol: "", value: formatted };
}

function BagIcon() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M8 8V6a4 4 0 0 1 8 0v2" />
      <rect x="5" y="8" width="14" height="13" rx="2.5" />
      <path d="M9 13h6" />
    </svg>
  );
}
